import axios from 'axios';
import { apiClient as api } from './apiClient';

// ═══════════════════════════════════════════════════════════════
// Casos de alta (F1.4, GESCL-T01) — contrato del backend
// (AltasCasosController + AltasClientesBusquedaController).
// No modificar sin coordinar con el backend.
// ═══════════════════════════════════════════════════════════════

/** Estados del caso — IDs del catálogo FC_C_ALTA_CASO_ESTADO (spec §2). */
export enum EstadoCaso {
  Pendiente = 1,
  EnEspera = 2,
  RegistroValidado = 3,
  ClienteExistenteActualizado = 4,
  RegistroEliminado = 5, // en UI se etiqueta "Descartada" (ajuste 1)
  AltaDirecta = 6,
}

/** Orígenes del caso — IDs del catálogo FC_C_ALTA_CASO_ORIGEN (D24). */
export enum OrigenCaso {
  Web = 1,
  Manual = 2,
  Asesor = 3,
}

export interface CasoAltaResumen {
  id: number;
  fechaAlta: string;
  estado: EstadoCaso;
  dsEstado: string;
  esFinal: boolean;
  origen: OrigenCaso;
  dsOrigen: string;
  idCodigoGf?: string | null;
  nombre?: string | null;
  apellido1?: string | null;
  apellido2?: string | null;
  email?: string | null;
  emailVerificado?: boolean | null;
  ultimaNota?: string | null;
  fechaUltimaNota?: string | null;
}

export interface CasoAltaPagina {
  casos: CasoAltaResumen[];
  totalFiltrado: number;
  pagina: number;
  tamanio: number;
  /**
   * Conteo por estado (id de estado → nº de casos) para los badges de los filtros internos
   * (mockup validado, ajuste 1). Solo llega poblado en la pestaña "Por tratar" (listado corto);
   * en "Tratados" viene vacío a propósito (conteo caro sobre un listado que crece).
   */
  contadoresPorEstado: Record<number, number>;
}

export interface CasoAltaNota {
  id: number;
  fecha: string;
  autor: string;
  texto: string;
}

export interface CasoAltaTransicion {
  id: number;
  estadoOrigen?: EstadoCaso | null;
  estadoDestino: EstadoCaso;
  fecha: string;
  autor: string;
  idNota?: number | null;
  excepcionSinEmail: boolean;
}

export interface CasoAltaDetalle extends Omit<CasoAltaResumen, 'ultimaNota' | 'fechaUltimaNota'> {
  idCliente?: number | null;
  idClienteWeb?: number | null; // ID de la web/GFW (D-GC-11), no Auraquantic
  idTratamiento?: number | null;
  anioNacimiento?: number | null;
  prefijoMovil?: string | null;
  movil?: string | null;
  idProvincia?: number | null;
  codigoPostal?: string | null;
  idPais?: number | null;
  inversor: boolean;
  idPlazoCompra?: number | null;
  boletin: boolean;
  notasProcedencia?: string | null;
  fechaVerifConsulta?: string | null;
  fechaFinalizacion?: string | null;
  tramosFacturacion: number[];
  provinciasInteres: number[];
  notas: CasoAltaNota[];
  transiciones: CasoAltaTransicion[];
}

/** Columna izquierda del tratamiento (admite datos incompletos: la obligatoriedad se comprueba al ejecutar). */
export interface GuardarCasoRequest {
  idTratamiento?: number | null;
  nombre?: string | null;
  apellido1?: string | null;
  apellido2?: string | null;
  anioNacimiento?: number | null;
  email?: string | null;
  prefijoMovil?: string | null;
  movil?: string | null;
  idProvincia?: number | null;
  codigoPostal?: string | null;
  idPais?: number | null;
  inversor: boolean;
  idPlazoCompra?: number | null;
  boletin: boolean;
  notasProcedencia?: string | null;
  tramosFacturacion: number[];
  provinciasInteres: number[];
}

export interface ValidarCasoRequest {
  datos: GuardarCasoRequest;
  nota?: string | null;
  excepcionSinEmail: boolean;
}

export interface ValidarCasoResultado {
  idCaso: number;
  idCliente: number;
  codigoCl: string;
  idOportunidad?: number | null;
  geolocalizacionResuelta: boolean;
}

/** Salida 2 — Actualizar existente y eliminar provisional (GESCL-T02). */
export interface ActualizarExistenteRequest {
  datos: GuardarCasoRequest;
  idClienteExistente: number;
  nota?: string | null;
  excepcionSinEmail: boolean;
}

export interface ActualizarExistenteResultado {
  idCaso: number;
  idClienteExistente: number;
  codigoExistente: string;
  idOportunidad?: number | null;
  geolocalizacionResuelta: boolean;
}

/** Salida 3 — Descartar (GESCL-T02). */
export interface DescartarCasoRequest {
  nota?: string | null;
}

export interface DescartarCasoResultado {
  idCaso: number;
}

export interface ClienteCoincidente {
  idCliente: number;
  idCodigoGf?: string | null;
  nombre?: string | null;
  apellido1?: string | null;
  apellido2?: string | null;
  provincia?: string | null;
  telefono?: string | null;
  email?: string | null;
  porCodigo: boolean;
  porEmail: boolean;
  porTelefono: boolean;
  porMovil: boolean;
  porNombre: boolean;
}

export interface CoincidentesResultado {
  porCodigo: number;
  porEmail: number;
  porTelefono: number;
  porMovil: number;
  porNombre: number;
  candidatos: ClienteCoincidente[];
}

export interface CatalogoItem {
  id: number;
  descripcion: string;
  codigo?: string | null;
}

export interface CatalogosAltas {
  tratamientos: CatalogoItem[];
  plazosCompra: CatalogoItem[];
  tramosFacturacion: CatalogoItem[];
  provincias: CatalogoItem[];
}

export interface ClienteExistenteFicha {
  idCliente: number;
  idClienteGf?: number | null;
  idClienteWeb?: number | null; // ID de la web/GFW (D-GC-11); no lo da GC_pr_CLIENTE_READ, se lee aparte
  idCodigoGf?: string | null;
  nombre?: string | null;
  apellido1?: string | null;
  apellido2?: string | null;
  telefonoMovil?: string | null;
  email?: string | null;
  direccion?: string | null;
  poblacion?: string | null;
  codigoPostal?: string | null;
  idProvincia?: number | null;
  provincia?: string | null;
  tieneFichaCompra: boolean;
  fichaAlta?: string | null;
  fichaBaja?: string | null;
  idPlazoCompra?: number | null;
  plazoCompra?: string | null;
  provinciasInteres: CatalogoItem[];
  tramosFacturacion: CatalogoItem[];
}

export interface ListarCasosParams {
  tratados: boolean;
  estado?: EstadoCaso;
  origen?: OrigenCaso;
  q?: string;
  pagina?: number;
  tamanio?: number;
}

// ------------------------------------------------------------------ llamadas

export const listarCasos = async (params: ListarCasosParams): Promise<CasoAltaPagina> => {
  const res = await api.get('/api/altas/casos', { params });
  return res.data;
};

export const leerCaso = async (idCaso: number): Promise<CasoAltaDetalle> => {
  const res = await api.get(`/api/altas/casos/${idCaso}`);
  return res.data;
};

export const guardarCaso = async (idCaso: number, datos: GuardarCasoRequest): Promise<void> => {
  await api.put(`/api/altas/casos/${idCaso}`, datos);
};

export const anadirNota = async (idCaso: number, texto: string): Promise<void> => {
  await api.post(`/api/altas/casos/${idCaso}/notas`, { texto });
};

export const cambiarEspera = async (idCaso: number, enEspera: boolean, nota?: string): Promise<void> => {
  await api.post(`/api/altas/casos/${idCaso}/en-espera`, { enEspera, nota });
};

export const validarCaso = async (idCaso: number, req: ValidarCasoRequest): Promise<ValidarCasoResultado> => {
  const res = await api.post(`/api/altas/casos/${idCaso}/validar`, req);
  return res.data;
};

export const actualizarExistente = async (
  idCaso: number, req: ActualizarExistenteRequest,
): Promise<ActualizarExistenteResultado> => {
  const res = await api.post(`/api/altas/casos/${idCaso}/actualizar-existente`, req);
  return res.data;
};

export const descartarCaso = async (
  idCaso: number, req: DescartarCasoRequest,
): Promise<DescartarCasoResultado> => {
  const res = await api.post(`/api/altas/casos/${idCaso}/descartar`, req);
  return res.data;
};

export const buscarCoincidentes = async (criterios: {
  codigo?: string; email?: string; telefono?: string; movil?: string; nombre?: string;
  /** Cliente provisional del propio candidato a excluir del resultado. */
  idClienteExcluir?: number;
}): Promise<CoincidentesResultado> => {
  // POST (no GET): los criterios son PII y no deben viajar en la query string / logs de acceso.
  const res = await api.post('/api/altas/clientes/coincidentes', criterios);
  return res.data;
};

export const leerFichaExistente = async (codigoGf: string): Promise<ClienteExistenteFicha> => {
  const res = await api.get(`/api/altas/clientes/existentes/${encodeURIComponent(codigoGf)}`);
  return res.data;
};

export const leerCatalogos = async (): Promise<CatalogosAltas> => {
  const res = await api.get('/api/altas/clientes/catalogos');
  return res.data;
};

// ------------------------------------------------------------------ errores

/**
 * Resultado de interpretar un error del backend para presentarlo al usuario.
 * `faltas` llega relleno en el 422 de las salidas (ajuste 12): lista de requisitos
 * incumplidos que el FE presenta en un Modal.warning con detalle en lista.
 */
export interface ErrorAccionable {
  mensaje: string;
  faltas?: string[];
}

/**
 * Traduce errores HTTP a mensaje accionable SIN volcar `ProblemDetails.detail` crudo
 * en respuestas 5xx (deuda RF-13): en errores de servidor el detail puede llevar
 * información interna, así que solo se usa el detail/title en 4xx (mensajes de negocio
 * redactados por el backend para el usuario).
 */
export const interpretarError = (error: unknown): ErrorAccionable => {
  if (axios.isAxiosError(error) && error.response) {
    const { status, data } = error.response as {
      status: number;
      data?: { title?: string; detail?: string; faltas?: string[]; errors?: Record<string, string[]> };
    };

    if (status === 422 && Array.isArray(data?.faltas)) {
      return { mensaje: data?.title ?? 'No se puede completar la acción todavía.', faltas: data!.faltas };
    }

    if (status === 400 && data?.errors) {
      const detalles = Object.values(data.errors).flat();
      if (detalles.length > 0) return { mensaje: 'Revise los datos introducidos.', faltas: detalles };
    }

    if (status >= 400 && status < 500) {
      return { mensaje: data?.detail || data?.title || 'No se ha podido completar la operación.' };
    }
  }

  return { mensaje: 'No se ha podido completar la operación. Inténtalo de nuevo en unos minutos.' };
};
